import { useBulkUpload } from '@hook'
import { Button, Modal } from '@ui'
import { getError, showToast } from '@util'
import { CheckBox } from 'grommet'
import { useEffect, useMemo, useRef, useState } from 'react'
import { PATIENT_BULK_UPLOAD_FIELDS } from '@/src/constants/data'
import {
  autoMapHeaders,
  escapeCsvCell,
  parseCsv,
  type ColumnMap,
  type ParsedCsv
} from '@/src/pages/Pharmacist/PharmacyWorkspace/utils/csv'
import { ActionRow, SmButton, StyledSelect } from '@/src/pages/Pharmacist/PharmacyWorkspace/panels/shared'

const mapableFields = PATIENT_BULK_UPLOAD_FIELDS
const requiredMapFields = ['first_name', 'last_name', 'gender', 'date_of_birth'] as const
const apiHeaders = [
  'first_name',
  'middle_name',
  'last_name',
  'gender',
  'email',
  'date_of_birth',
  'phone_number',
  'ref',
  'force_create'
] as const

type Props = {
  onClose: () => void
  isLoading?: boolean
}

type Step = 'upload' | 'map'

const buildCanonicalCsv = ({
  headers,
  rows,
  columnMap,
  forceCreate
}: {
  headers: string[]
  rows: string[][]
  columnMap: ColumnMap
  forceCreate: boolean
}) => {
  const headerIndex = new Map(headers.map((h, i) => [h, i]))
  const forceCreateValue = forceCreate ? 'true' : 'false'

  const lines = [
    apiHeaders.join(','),
    ...rows.map(row => {
      const get = (name: string) => {
        const srcHeader = columnMap[name]
        if (!srcHeader) return ''
        const idx = headerIndex.get(srcHeader)
        if (idx === undefined) return ''
        return String(row[idx] ?? '').trim()
      }

      const values: Record<string, string> = {
        first_name: get('first_name'),
        middle_name: get('middle_name'),
        last_name: get('last_name'),
        gender: get('gender'),
        email: get('email'),
        date_of_birth: get('date_of_birth'),
        phone_number: get('phone_number'),
        ref: get('ref'),
        force_create: forceCreateValue
      }

      return apiHeaders.map(h => escapeCsvCell(values[h] ?? '')).join(',')
    })
  ]

  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' })
  return new File([blob], 'patient-bulk-mapped.csv', { type: 'text/csv' })
}

const PatientBulkMapModal = ({ onClose, isLoading }: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { bulkImport, isLoading: importLoading } = useBulkUpload({
    context: 'patients',
    invalidateQueryKey: ['patients']
  })

  const [step, setStep] = useState<Step>('upload')
  const [fileName, setFileName] = useState('')
  const [parsed, setParsed] = useState<ParsedCsv | null>(null)
  const [columnMap, setColumnMap] = useState<ColumnMap>({})
  const [forceCreate, setForceCreate] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const csvHeaderOptions = useMemo(() => {
    if (!parsed) return []
    return [{ label: '— Ignore —', value: '' }, ...parsed.headers.map(h => ({ label: h, value: h }))]
  }, [parsed])

  const canImport = Boolean(parsed?.rows.length) && requiredMapFields.every(field => Boolean(columnMap[field]))

  useEffect(() => {
    if (parsed) {
      setColumnMap(autoMapHeaders(parsed.headers, mapableFields))
    }
  }, [parsed])

  const handleFile = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      showToast('Please select a CSV file')
      return
    }

    try {
      const text = await file.text()
      const next = parseCsv(text)
      if (!next.headers.length) {
        showToast('CSV has no header row')
        return
      }
      if (!next.rows.length) {
        showToast('CSV has no data rows')
        return
      }

      setFileName(file.name)
      setParsed(next)
      setStep('map')
    } catch {
      showToast('Could not read CSV file')
    }
  }

  const handleImport = async () => {
    if (!parsed || !canImport) return

    setSubmitting(true)
    try {
      const file = buildCanonicalCsv({
        headers: parsed.headers,
        rows: parsed.rows,
        columnMap,
        forceCreate
      })
      const res = await bulkImport(file)
      const created = res?.meta?.created_count ?? 0
      const failed = res?.meta?.failed_rows?.length ?? 0
      showToast(`Patient upload complete: ${created} created, ${failed} failed`)
      onClose()
    } catch (error) {
      getError(error)
      throw error
    } finally {
      setSubmitting(false)
    }
  }

  const busy = Boolean(isLoading || importLoading || submitting)

  return (
    <Modal onClose={onClose} title="Bulk patient upload">
      <div className="flex max-h-[min(70vh,640px)] flex-col gap-4 overflow-y-auto p-6">
        {step === 'upload' ? (
          <>
            <p className="m-0 text-sm text-[#5C6C71]">
              Upload a CSV, map each column to the patient fields, then import.
            </p>
            <input
              accept=".csv,text/csv"
              className="hidden"
              onChange={e => {
                const file = e.target.files?.[0]
                e.target.value = ''
                if (file) void handleFile(file)
              }}
              ref={fileInputRef}
              type="file"
            />
            <ActionRow>
              <SmButton kinds="secondary" label="Select CSV file" onClick={() => fileInputRef.current?.click()} />
              <Button kinds="outline" label="Cancel" onClick={onClose} size="wide" />
            </ActionRow>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="m-0 text-sm text-[#5C6C71]">
                <span className="font-medium text-[#1A1A1A]">{fileName}</span>
                {parsed ? ` · ${parsed.rows.length} row${parsed.rows.length === 1 ? '' : 's'}` : ''}
              </p>
              <SmButton
                disabled={busy}
                kinds="outline"
                label="Choose another file"
                onClick={() => {
                  setStep('upload')
                  setParsed(null)
                  setColumnMap({})
                  setForceCreate(false)
                  setFileName('')
                }}
              />
            </div>

            <div>
              <p className="mb-2 mt-0 text-sm font-medium text-[#1A1A1A]">Map CSV columns</p>
              <div className="flex max-h-[min(320px,40vh)] flex-col gap-2 overflow-y-auto rounded-lg border border-[#E6E6E6] p-3">
                {mapableFields.map(field => {
                  const required = requiredMapFields.includes(field.name as (typeof requiredMapFields)[number])
                  return (
                    <div
                      className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]"
                      key={field.name}
                    >
                      <span className="text-sm text-[#1A1A1A]">
                        {field.label}
                        {required ? ' *' : ''}
                      </span>
                      <StyledSelect
                        disabled={busy}
                        onChange={({ value }) => setColumnMap(prev => ({ ...prev, [field.name]: value }))}
                        options={csvHeaderOptions}
                        value={columnMap[field.name] ?? ''}
                      />
                    </div>
                  )
                })}
              </div>
            </div>

            <CheckBox
              checked={forceCreate}
              disabled={busy}
              label="Force create if duplicate"
              onChange={e => setForceCreate(e.target.checked)}
            />

            <ActionRow>
              <SmButton
                disabled={!canImport || busy}
                kinds="primary"
                label={busy ? 'Importing…' : 'Import'}
                onClick={() => void handleImport()}
              />
              <Button disabled={busy} kinds="secondary" label="Cancel" onClick={onClose} size="wide" />
            </ActionRow>
          </>
        )}
      </div>
    </Modal>
  )
}

export default PatientBulkMapModal
